/**
 * Axios API Client with Authentication Interceptors
 *
 * Configured to:
 * - Add Bearer tokens to all requests
 * - Handle 401 Unauthorized errors
 * - Communicate with FastAPI backend
 */

import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 75000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token getter function (will be set by setupInterceptors)
let getTokenFn: (() => Promise<string>) | null = null;

/**
 * Setup authentication interceptors
 * Must be called with getAccessToken function from useAuth hook
 */
export const setupAuthInterceptors = (getAccessToken: () => Promise<string>) => {
  getTokenFn = getAccessToken;
};

/**
 * Request interceptor: Add Bearer token to all requests
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Inject contextual login type header for 3-button SSO architecture
    const intent = sessionStorage.getItem('onboarding_intent');
    if (intent === 'enroll') {
      config.headers['X-Login-Type'] = 'Button1';
    } else if (intent === 'join') {
      config.headers['X-Login-Type'] = 'Button2';
    } else if (intent === 'app_login') {
      config.headers['X-Login-Type'] = 'Button3';
    }

    // Only add token if interceptors have been setup
    if (getTokenFn) {
      try {
        const token = await getTokenFn();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get access token:', error);
        // Continue with request without token (will fail at backend with 401)
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor: Handle 401 errors
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized account (Microsoft authenticated but backend rejected)
      // Redirect to access-denied to break the login -> / -> 401 loop
      console.error('Unauthorized account/session, redirecting to access-denied');
      window.location.href = '/access-denied';
    }
    return Promise.reject(error);
  }
);

export default apiClient;
